import React, { useMemo, useState } from "react";
import { dimensionMetrics } from "../utils/shipmentMeasurements";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { submitQuote } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Calculator, X } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import ShippingInfoModal, { ShippingInfoButton } from "./ShippingInfoModal";

const SHIPMENT_TYPES = [
  { value: "pakket", label: "Pakket" },
  { value: "doos", label: "Doos" },
  { value: "pallet", label: "Pallet" },
  { value: "meubels", label: "Meubels" },
  { value: "verhuizing", label: "Verhuizing" },
  { value: "voertuig", label: "Voertuig" },
  { value: "shop_drop_ship", label: "Shop, Drop & Ship" },
  { value: "zakelijk", label: "Zakelijk / B2B" },
  { value: "overig", label: "Overig" },
];

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
}

export default function QuoteForm({ defaultService = "sea-freight", compact = false }) {
  const { t } = useI18n();
  const SERVICES = [
    { value: "air-freight", label: t("services.air_freight") },
    { value: "sea-freight", label: t("services.sea_freight") },
    { value: "moving", label: t("services.moving") },
    { value: "shop-drop-ship", label: t("services.shop_drop_ship") },
    { value: "package-forwarding", label: "Package Forwarding" },
    { value: "other", label: "Other / Not sure" },
  ];

  const [form, setForm] = useState(() => {
    const base = {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      service: defaultService,
      destination: "Curaçao",
      shipment_type: "",
      quantity: 1,
      length_cm: "",
      width_cm: "",
      height_cm: "",
      weight_kg: "",
      description: "",
    };
    if (typeof window === "undefined") return base;
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.get("prefill")) return base;
      const raw = window.sessionStorage.getItem("quote_prefill_payload");
      if (!raw) return base;
      const payload = JSON.parse(raw);
      if (!payload || typeof payload !== "object") return base;
      return {
        ...base,
        first_name: payload.first_name ?? base.first_name,
        last_name: payload.last_name ?? base.last_name,
        quantity: payload.quantity ?? base.quantity,
        length_cm: payload.length_cm ?? base.length_cm,
        width_cm: payload.width_cm ?? base.width_cm,
        height_cm: payload.height_cm ?? base.height_cm,
        weight_kg: payload.weight_kg ?? base.weight_kg,
        destination: payload.destination || base.destination,
        shipment_type: payload.shipment_type || base.shipment_type,
        service: payload.service || base.service,
        description: payload.description || base.description,
      };
    } catch {
      return base;
    }
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [prefillSource, setPrefillSource] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const flag = params.get("prefill");
      if (!flag) return null;
      const raw = window.sessionStorage.getItem("quote_prefill_payload");
      if (!raw) return null;
      const payload = JSON.parse(raw);
      return payload?.source || flag;
    } catch {
      return null;
    }
  });

  const clearPrefill = () => {
    try {
      window.sessionStorage.removeItem("quote_prefill_payload");
    } catch { /* ignore */ }
    setPrefillSource(null);
    setForm((f) => ({
      ...f,
      quantity: 1,
      length_cm: "",
      width_cm: "",
      height_cm: "",
      weight_kg: "",
      description: "",
    }));
  };

  // ---------- validation helpers ----------
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateRequiredClient = (f) => {
    const e = {};
    if (!f.first_name.trim()) e.first_name = "Vul uw voornaam in.";
    if (!f.last_name.trim()) e.last_name = "Vul uw achternaam in.";
    if (!f.email.trim()) e.email = "Vul uw e-mailadres in.";
    else if (!EMAIL_RE.test(f.email.trim())) e.email = "Vul een geldig e-mailadres in.";
    if (!f.phone.trim()) e.phone = "Vul uw telefoonnummer in.";
    return e;
  };

  // Compute CBM / volumetric / chargeable weight live in the form so the user
  // can sanity-check before submitting. Backend recomputes server-side too.
  const derived = useMemo(() => {
    const L = n(form.length_cm);
    const W = n(form.width_cm);
    const H = n(form.height_cm);
    const Q = n(form.quantity) || 1;
    const G = n(form.weight_kg);
    if (!L || !W || !H) return { cbm: 0, vol: 0, gross: G, charge: 0, basis: "" };
    const calculated = dimensionMetrics({ length: L, width: W, height: H, quantity: Q });
    const cbm = Math.round(calculated.cbm * 10000) / 10000;
    const vol = Math.round(calculated.volumetric * 100) / 100;
    const charge = Math.max(G, vol);
    const basis = vol >= G && charge > 0 ? "volumetric_weight" : "gross_weight";
    return { cbm, vol, gross: G, charge: Math.round(charge * 100) / 100, basis };
  }, [form.length_cm, form.width_cm, form.height_cm, form.quantity, form.weight_kg]);

  const onChange = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Verplichte klantvelden — voornaam, achternaam, e-mail, telefoon
    const required = validateRequiredClient(form);
    if (Object.keys(required).length) {
      setErrors(required);
      toast.error("Controleer de verplichte velden hierboven.");
      // focus the first missing field
      const firstKey = Object.keys(required)[0];
      try {
        const el = document.querySelector(`[data-testid="quote-input-${firstKey.replace("_", "-")}"]`);
        if (el && typeof el.focus === "function") el.focus();
      } catch { /* ignore */ }
      return;
    }
    if (!form.service) {
      toast.error("Selecteer een service.");
      return;
    }
    if (!form.shipment_type) {
      toast.error("Selecteer een type zending.");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Geef een korte omschrijving van uw goederen.");
      return;
    }
    if (n(form.length_cm) <= 0 || n(form.width_cm) <= 0 || n(form.height_cm) <= 0 || n(form.weight_kg) <= 0) {
      toast.error("Vul lengte, breedte, hoogte en gewicht in zodat wij uw offerte correct kunnen berekenen.");
      return;
    }
    if (n(form.quantity) <= 0) {
      toast.error("Vul het aantal pakketten/dozen in.");
      return;
    }
    setLoading(true);
    try {
      const full_name = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      const payload = {
        // Phase P0 — gestructureerde klant-naamvelden (verplicht), met
        // backwards-compatible ``name`` voor oude admin views/integraties
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        name: full_name,
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service,
        destination: form.destination,
        description: form.description,
        shipment_type: form.shipment_type,
        method: form.service,
        quantity: Number(form.quantity),
        length_cm: Number(form.length_cm),
        width_cm: Number(form.width_cm),
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        // legacy fields for backward compatibility with older backend listeners
        weight: `${form.weight_kg} kg`,
        volume: `${derived.cbm} m³`,
        // structured derived values — backend recomputes too
        cbm: derived.cbm,
        volumetric_weight: derived.vol,
        chargeable_weight: derived.charge,
        chargeable_weight_basis: derived.basis,
      };
      await submitQuote(payload);
      setSuccess(true);
      // Clear the prefill payload once we've successfully submitted so
      // we don't replay stale data on the next form visit.
      try {
        window.sessionStorage.removeItem("quote_prefill_payload");
      } catch { /* ignore */ }
      setPrefillSource(null);
      toast.success("Offerte-aanvraag verzonden. Wij reageren binnen 1 werkuur.");
    } catch (err) {
      toast.error("Verzenden mislukte. Probeer opnieuw of stuur een WhatsApp-bericht.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="quote-success" className="card-premium p-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h3 className="font-chivo font-black text-2xl text-brand-900 mb-2">{t("quote.success_title")}</h3>
        <p className="text-ink-500 text-sm">{t("quote.success_desc")}</p>
        <Button
          data-testid="quote-success-reset"
          onClick={() => {
            setSuccess(false);
            setErrors({});
            setForm({
              first_name: "", last_name: "", email: "", phone: "",
              service: defaultService, destination: "Curaçao",
              shipment_type: "", quantity: 1,
              length_cm: "", width_cm: "", height_cm: "", weight_kg: "",
              description: "",
            });
          }}
          className="mt-7 btn-dark"
        >
          {t("quote.submit_another")}
        </Button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={onSubmit}
      noValidate
      className={`card-premium ${compact ? "p-6" : "p-8"} space-y-5`}
    >
      {/* Auto-open shipping-info modal once per user when they reach the form */}
      <ShippingInfoModal autoOpen={!prefillSource} />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-chivo font-black text-2xl text-[#082c50]">{t("quote.title")}</h3>
          <p className="text-sm text-gray-500 mt-1">{t("quote.subtitle")}</p>
        </div>
        <ShippingInfoButton testId="quote-shipping-info-trigger" />
      </div>

      {prefillSource && (
        <div
          className="rounded-xl border-2 border-[#1a5193]/30 bg-[#1a5193]/5 p-4"
          data-testid="quote-prefill-banner"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#1a5193] text-white flex items-center justify-center">
              <Calculator className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#1a5193]">
                Voorberekende zending
              </div>
              <div className="text-sm font-semibold text-[#082c50] mt-0.5">
                {prefillSource === "cbm_calculator"
                  ? "Maten en CBM zijn automatisch ingevuld vanuit de CBM Calculator."
                  : "Maten zijn automatisch ingevuld vanuit een rekentool."}
              </div>
            </div>
            <button
              type="button"
              onClick={clearPrefill}
              className="flex-shrink-0 w-7 h-7 rounded-md hover:bg-[#082c50]/10 flex items-center justify-center text-[#082c50]"
              aria-label="Sluit voorberekende-banner"
              data-testid="quote-prefill-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Compacte samenvattings-tabel: exact dezelfde waarden die naar
              backend/e-mail/admin gaan. Geeft klant zekerheid dat de
              calculator-input correct is overgenomen. */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]" data-testid="quote-prefill-summary">
            <div className="bg-white border border-[#1a5193]/15 px-2.5 py-1.5 rounded-md">
              <div className="text-[9px] uppercase tracking-wide text-gray-500">Aantal</div>
              <div className="font-mono font-bold text-[#082c50]" data-testid="quote-prefill-quantity">{form.quantity || "—"}</div>
            </div>
            <div className="bg-white border border-[#1a5193]/15 px-2.5 py-1.5 rounded-md col-span-2 sm:col-span-2">
              <div className="text-[9px] uppercase tracking-wide text-gray-500">Afmetingen (L × B × H)</div>
              <div className="font-mono font-bold text-[#082c50]" data-testid="quote-prefill-dims">
                L{form.length_cm || "—"} × B{form.width_cm || "—"} × H{form.height_cm || "—"} cm
              </div>
            </div>
            <div className="bg-white border border-[#1a5193]/15 px-2.5 py-1.5 rounded-md">
              <div className="text-[9px] uppercase tracking-wide text-gray-500">Werkelijk gewicht</div>
              <div className="font-mono font-bold text-[#082c50]" data-testid="quote-prefill-gross">{form.weight_kg || "—"} kg</div>
            </div>
            <div className="bg-white border border-[#1a5193]/15 px-2.5 py-1.5 rounded-md">
              <div className="text-[9px] uppercase tracking-wide text-gray-500">Volume (CBM)</div>
              <div className="font-mono font-bold text-[#082c50]" data-testid="quote-prefill-cbm">{derived.cbm || 0} m³</div>
            </div>
            <div className="bg-white border border-[#1a5193]/15 px-2.5 py-1.5 rounded-md">
              <div className="text-[9px] uppercase tracking-wide text-gray-500">Belastbaar gewicht</div>
              <div className="font-mono font-bold text-[#082c50]" data-testid="quote-prefill-chargeable">{derived.charge || 0} kg</div>
            </div>
          </div>

          <div className="text-xs text-[#514e4e] mt-2">
            Klopt iets niet?{" "}
            <button
              type="button"
              onClick={clearPrefill}
              className="underline font-semibold text-[#1a5193]"
              data-testid="quote-prefill-clear"
            >
              Wis voorberekende gegevens
            </button>{" "}
            of pas de velden hieronder aan.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
            Voornaam <span className="text-red-600">*</span>
          </Label>
          <Input
            data-testid="quote-input-first-name"
            required
            value={form.first_name}
            onChange={(e) => onChange("first_name", e.target.value)}
            className={`mt-1 rounded-xl focus-visible:ring-[#1a5193] ${errors.first_name ? "border-red-500" : "border-gray-300"}`}
            placeholder="John"
            aria-invalid={!!errors.first_name}
            aria-describedby={errors.first_name ? "quote-error-first-name" : undefined}
          />
          {errors.first_name && (
            <p id="quote-error-first-name" data-testid="quote-error-first-name" className="text-xs text-red-600 mt-1">{errors.first_name}</p>
          )}
        </div>
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
            Achternaam <span className="text-red-600">*</span>
          </Label>
          <Input
            data-testid="quote-input-last-name"
            required
            value={form.last_name}
            onChange={(e) => onChange("last_name", e.target.value)}
            className={`mt-1 rounded-xl focus-visible:ring-[#1a5193] ${errors.last_name ? "border-red-500" : "border-gray-300"}`}
            placeholder="Doe"
            aria-invalid={!!errors.last_name}
            aria-describedby={errors.last_name ? "quote-error-last-name" : undefined}
          />
          {errors.last_name && (
            <p id="quote-error-last-name" data-testid="quote-error-last-name" className="text-xs text-red-600 mt-1">{errors.last_name}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
            {t("quote.email")} <span className="text-red-600">*</span>
          </Label>
          <Input
            data-testid="quote-input-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => onChange("email", e.target.value)}
            className={`mt-1 rounded-xl ${errors.email ? "border-red-500" : "border-gray-300"}`}
            placeholder="you@email.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "quote-error-email" : undefined}
          />
          {errors.email && (
            <p id="quote-error-email" data-testid="quote-error-email" className="text-xs text-red-600 mt-1">{errors.email}</p>
          )}
        </div>
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">
            {t("quote.phone")} <span className="text-red-600">*</span>
          </Label>
          <Input
            data-testid="quote-input-phone"
            required
            value={form.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            className={`mt-1 rounded-xl ${errors.phone ? "border-red-500" : "border-gray-300"}`}
            placeholder="+31 6 ..."
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? "quote-error-phone" : undefined}
          />
          {errors.phone && (
            <p id="quote-error-phone" data-testid="quote-error-phone" className="text-xs text-red-600 mt-1">{errors.phone}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">{t("quote.service")} *</Label>
          <Select value={form.service} onValueChange={(v) => onChange("service", v)}>
            <SelectTrigger data-testid="quote-select-service" className="mt-1 rounded-xl border-gray-300">
              <SelectValue placeholder="Select service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`quote-service-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">{t("quote.destination")} *</Label>
          <Input data-testid="quote-input-destination" required value={form.destination} onChange={(e) => onChange("destination", e.target.value)} className="mt-1 rounded-xl border-gray-300" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div>
          <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">Type zending *</Label>
          <Select value={form.shipment_type} onValueChange={(v) => onChange("shipment_type", v)}>
            <SelectTrigger data-testid="quote-select-shipment-type" className="mt-1 rounded-xl border-gray-300">
              <SelectValue placeholder="Kies type zending" />
            </SelectTrigger>
            <SelectContent>
              {SHIPMENT_TYPES.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`quote-shipment-type-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 p-3 space-y-3" data-testid="quote-dims-block">
        <div className="text-xs uppercase tracking-wide font-bold text-[#082c50]">Afmetingen & gewicht * (verplicht)</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] uppercase">Aantal *</Label>
            <Input data-testid="quote-input-quantity" type="number" min="1" required value={form.quantity} onChange={(e) => onChange("quantity", e.target.value)} className="mt-1 rounded-xl border-gray-300" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Lengte (cm) *</Label>
            <Input data-testid="quote-input-length" type="number" min="0" step="0.01" required value={form.length_cm} onChange={(e) => onChange("length_cm", e.target.value)} className="mt-1 rounded-xl border-gray-300" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Breedte (cm) *</Label>
            <Input data-testid="quote-input-width" type="number" min="0" step="0.01" required value={form.width_cm} onChange={(e) => onChange("width_cm", e.target.value)} className="mt-1 rounded-xl border-gray-300" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Hoogte (cm) *</Label>
            <Input data-testid="quote-input-height" type="number" min="0" step="0.01" required value={form.height_cm} onChange={(e) => onChange("height_cm", e.target.value)} className="mt-1 rounded-xl border-gray-300" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Gewicht (kg) *</Label>
            <Input data-testid="quote-input-weight-kg" type="number" min="0" step="0.01" required value={form.weight_kg} onChange={(e) => onChange("weight_kg", e.target.value)} className="mt-1 rounded-xl border-gray-300" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div data-testid="quote-derived-cbm" className="bg-white border border-gray-200 px-3 py-2">
            <div className="text-[10px] uppercase text-gray-500">Volume (CBM)</div>
            <div className="font-mono font-bold text-[#082c50]">{derived.cbm || 0} m³</div>
          </div>
          <div data-testid="quote-derived-vol" className="bg-white border border-gray-200 px-3 py-2">
            <div className="text-[10px] uppercase text-gray-500">Volumetric weight</div>
            <div className="font-mono font-bold text-[#082c50]">{derived.vol || 0} kg</div>
          </div>
          <div data-testid="quote-derived-gross" className="bg-white border border-gray-200 px-3 py-2">
            <div className="text-[10px] uppercase text-gray-500">Werkelijk gewicht</div>
            <div className="font-mono font-bold text-[#082c50]">{derived.gross || 0} kg</div>
          </div>
          <div data-testid="quote-derived-charge" className="bg-white border border-[#1a5193] px-3 py-2">
            <div className="text-[10px] uppercase text-[#1a5193]">Belastbaar gewicht</div>
            <div className="font-mono font-bold text-[#1a5193]">{derived.charge || 0} kg</div>
          </div>
        </div>
        <p className="text-[11px] text-gray-500" data-testid="quote-whatever-greater-note">
          Wij rekenen met het hoogste gewicht: het werkelijke gewicht of het volumetrische gewicht. Dit heet &apos;whatever is greater&apos;.
          {derived.charge > 0 && (
            <> {" "}
              <span className="font-semibold text-[#082c50]">
                {derived.basis === "volumetric_weight" ? "Volumetrisch gewicht is leidend." : "Werkelijk gewicht is leidend."}
              </span>
            </>
          )}
        </p>
      </div>

      <div>
        <Label className="text-xs tracking-wide uppercase font-chivo font-bold text-gray-700">Omschrijving goederen *</Label>
        <Textarea
          data-testid="quote-input-description"
          required
          value={form.description}
          onChange={(e) => onChange("description", e.target.value)}
          rows={3}
          className="mt-1 rounded-xl border-gray-300"
          placeholder="Wat verzendt u? Wanneer? Eventueel speciale aandachtspunten."
        />
      </div>

      <Button
        data-testid="quote-submit-button"
        type="submit"
        disabled={loading}
        className="w-full bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-chivo font-bold tracking-wide uppercase text-sm py-6 shadow-brand-soft hover:shadow-brand-glow active:scale-[0.98] transition-all"
      >
        {loading ? t("quote.sending") : t("quote.submit")}
      </Button>
      <p className="text-[11px] text-gray-500 text-center">{t("quote.consent")}</p>
    </form>
  );
}
